/**
 * When auto-capture may fire.
 *
 * Pulled out of `useScaleAutoCapture` so the policy is testable on its own.
 * It decides whether a package gets created without anyone touching a keyboard,
 * which makes it worth exercising directly rather than through a renderer.
 */
import type { ScaleReading } from './hid-pos-scale';

/**
 * Whether a reading means the platter has been cleared.
 *
 * "Cleared" has to be judged three ways, because scales don't agree on how they
 * say it. HID scales send an explicit `stableAtZero`. Serial scales generally
 * don't, and one that only transmits settled readings never reports motion
 * either — so to those, an empty platter looks identical to a full one apart
 * from the number. Without the weight-based fallback such a scale would capture
 * exactly one package and then go dead for the rest of the shift.
 * @param reading - The latest reading.
 * @param minimumPounds - Threshold a settled weight must clear to count.
 * @returns Whether auto-capture should re-arm.
 */
export function shouldRearm(
  reading: ScaleReading,
  minimumPounds: number,
): boolean {
  // A null weight means an unreadable unit — there's no way to tell an empty
  // platter from a loaded one, so staying put is the safe answer.
  const empty = reading.pounds !== null && reading.pounds < minimumPounds;
  return (
    reading.status === 'stableAtZero' || reading.status === 'inMotion' || empty
  );
}

/**
 * Whether a reading is a weight worth committing.
 * @param reading - The latest reading.
 * @param minimumPounds - Threshold a settled weight must clear.
 * @param armed - Whether the platter has been cleared since the last capture.
 * @returns Whether to capture this reading.
 */
export function shouldCapture(
  reading: ScaleReading,
  minimumPounds: number,
  armed: boolean,
): boolean {
  return (
    armed &&
    reading.status === 'stable' &&
    reading.pounds !== null &&
    reading.pounds >= minimumPounds
  );
}
